'use client';

import React from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Tooltip,
  Typography,
  useTheme,
  type SelectChangeEvent,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import PanoramaHorizontalSelectIcon from '@mui/icons-material/PanoramaHorizontalSelect';
import CallSplitIcon from '@mui/icons-material/CallSplit';

export const FIELD_OPTIONS = [
  { value: 'pressure', label: 'Pressure' },
  { value: 'wss', label: 'Wall Shear Stress' },
  // STREAMLINES DISABLED - generation is not supported for now.
  // { value: 'velocity', label: 'Velocity (Streamlines)' },
] as const;

export type ViewFieldKey = (typeof FIELD_OPTIONS)[number]['value'];

type ControlPanelProps = {
  inlets: string[];
  inlet: string;
  fValue: number;
  fRange: [number, number];
  timeIndex: number;
  field: ViewFieldKey;
  onInletChange: (event: SelectChangeEvent<string>) => void | Promise<void>;
  onFChange: (_: Event, value: number | number[]) => void;
  onTimeIndexChange: (_: Event, value: number | number[]) => void;
  onFieldChange: (
    event: SelectChangeEvent<ViewFieldKey>,
  ) => void | Promise<void>;
  onPrevious: () => void;
  onNext: () => void;
  onSimulate: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
  onZoomStenosis: () => void;
  onZoomBifurcation: () => void;
  hideOverlay?: boolean;
};

const labelSx = {
  fontSize: '0.75rem',
  color: 'background.paper',
  mb: 0.25,
  VariantNumeric: 'tabular-nums',
};
const selectSx = {
  fontSize: '0.9rem',
  color: 'background.paper',
  '.MuiSelect-select': { py: 0.75 },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.15)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.3)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'primary.main',
  },
  '.MuiSelect-icon': { color: 'background.paper' },
};
const inputLabelSx = {
  fontSize: '1rem',
  color: 'background.paper',
  '&.MuiInputLabel-shrink': { fontSize: '1rem' },
};

function SliderGroup({
  label,
  value,
  onChange,
  min,
  max,
  step,
  valueLabelFormat,
  width,
  tooltip,
}: {
  label: string;
  value: number;
  onChange: (_: Event, v: number | number[]) => void;
  min: number;
  max: number;
  step: number;
  valueLabelFormat?: (v: number) => string;
  width?: number | string;
  tooltip?: string;
}) {
  return (
    <Box sx={{ width: width ?? 160, flexShrink: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        {tooltip ? (
          <Tooltip title={tooltip} arrow placement='top'>
            <Typography
              sx={{
                ...labelSx,
                borderBottom: '1px dotted rgba(255,255,255,0.4)',
                cursor: 'help',
                width: 'fit-content',
              }}
            >
              {label}
            </Typography>
          </Tooltip>
        ) : (
          <Typography sx={labelSx}>{label}</Typography>
        )}
      </Box>
      <Slider
        value={value}
        min={min}
        max={max}
        step={step}
        valueLabelDisplay='auto'
        valueLabelFormat={valueLabelFormat}
        onChange={onChange}
        size='small'
        sx={{
          py: 0,
          mt: 0.5,
          '& .MuiSlider-thumb': { width: 16, height: 16 },
          '& .MuiSlider-valueLabel': {
            fontSize: '0.8rem',
            lineHeight: 1,
            px: 0.8,
            py: 0.3,
          },
          '& .MuiSlider-track': { height: 6 },
          '& .MuiSlider-rail': { height: 6 },
        }}
      />
    </Box>
  );
}

export default function ControlPanel({
  inlets,
  inlet,
  fValue,
  fRange,
  timeIndex,
  field,
  onInletChange,
  onFChange,
  onTimeIndexChange,
  onFieldChange,
  onPrevious,
  onNext,
  onSimulate,
  canGoBack,
  canGoForward,
  onZoomStenosis,
  onZoomBifurcation,
  hideOverlay = false,
}: ControlPanelProps) {
  const theme = useTheme();

  // Enter key triggers simulate
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.repeat) return;
      const el = e.target as HTMLElement;
      // Skip when inside Select trigger (combobox) or its open dropdown (listbox)
      if (el.closest('[role="combobox"], [role="listbox"], .MuiPopover-root'))
        return;
      onSimulate();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onSimulate]);

  const timeStep = 0.008;
  const timeDisplayMax = 0.8;
  const timeSteps = Math.round(timeDisplayMax / timeStep); // 100
  const displayTime = (idx: number) => idx * timeStep;

  const t = displayTime(timeIndex);

  const fPct = Math.round(fValue * 100);
  const fStatus =
    fPct <= 70
      ? '⚠️ Low cardiac output, elevated graft thrombosis risk'
      : fPct <= 85
        ? 'Reduced perfusion'
        : fPct <= 95
          ? 'Mildly reduced, near resting baseline'
          : fPct <= 105
            ? 'Resting baseline'
            : fPct <= 115
              ? 'Mildly elevated demand'
              : fPct <= 125
                ? 'Elevated demand, light exertion'
                : '⚠️ Peak stress / hyperaemia';

  const timePhase =
    t < 0.05
      ? 'End diastole, pre-systolic'
      : t < 0.2
        ? 'Early systole'
        : t < 0.35
          ? 'Peak systole'
          : t < 0.5
            ? 'End systole, isovolumetric relaxation'
            : t < 0.65
              ? 'Early diastole'
              : t < 0.75
                ? 'Diastolic filling'
                : 'End of cycle';

  const btnSx = {
    fontSize: '0.82rem',
    whiteSpace: 'nowrap',
    minWidth: 0,
    py: 0.35,
    px: 1.25,
    color: 'background.paper',
    borderColor: 'rgba(255,255,255,0.2)',
    '&:hover': {
      borderColor: 'rgba(255,255,255,0.5)',
      bgcolor: 'rgba(255,255,255,0.08)',
    },
    '&.Mui-disabled': {
      borderColor: 'rgba(255,255,255,0.08)',
      color: 'rgba(255,255,255,0.25)',
    },
  } as const;

  return (
    <Box
      sx={{
        px: 3,
        py: 1.5,
        borderRadius: 3,
        bgcolor: 'rgba(15,15,20,0.82)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
      }}
    >
      {/* Row 1 — parameter inputs (no wrap) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 2,
          flexWrap: 'nowrap',
          justifyContent: 'center',
        }}
      >
        {/* Inlet */}
        <FormControl size='small' sx={{ minWidth: 110 }}>
          <InputLabel id='inlet-select-label' sx={inputLabelSx}>
            Inlet
          </InputLabel>
          <Select
            labelId='inlet-select-label'
            value={inlet}
            label='Inlet'
            onChange={onInletChange}
            sx={selectSx}
            MenuProps={{
              anchorOrigin: { vertical: 'top', horizontal: 'left' },
              transformOrigin: { vertical: 'bottom', horizontal: 'left' },
            }}
          >
            {inlets.map((name) => (
              <MenuItem key={name} value={name} sx={{ fontSize: '0.8rem' }}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* f slider */}
        <SliderGroup
          label={`Coronary Flow Demand: ${Math.round(fValue * 100)}%`}
          value={fValue}
          onChange={onFChange}
          min={fRange[0]}
          max={fRange[1]}
          step={(fRange[1] - fRange[0]) / 100}
          valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
          width={180}
          tooltip='% of patient resting baseline'
        />

        {/* Time slider */}
        <SliderGroup
          label={`Cardiac Cycle Phase: ${displayTime(timeIndex).toFixed(3)} s`}
          value={timeIndex}
          onChange={onTimeIndexChange}
          min={0}
          max={timeSteps}
          step={1}
          valueLabelFormat={(v) => `${displayTime(v as number).toFixed(3)} s`}
          width={180}
          tooltip='Position within one heartbeat  (T = 0.8 s,  HR = 75 bpm)'
        />

        {/* Field */}
        <FormControl size='small' sx={{ minWidth: 200 }}>
          <InputLabel id='field-select-label' sx={inputLabelSx}>
            Field
          </InputLabel>
          <Select
            labelId='field-select-label'
            value={field}
            label='Field'
            onChange={onFieldChange}
            sx={selectSx}
            MenuProps={{
              anchorOrigin: { vertical: 'top', horizontal: 'left' },
              transformOrigin: { vertical: 'bottom', horizontal: 'left' },
            }}
          >
            {FIELD_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                sx={{ fontSize: '0.8rem' }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Separator */}
        <Box
          sx={{
            width: '1px',
            height: 32,
            bgcolor: 'rgba(255,255,255,0.12)',
            mx: 0.5,
          }}
        />

        {/* Zoom buttons (icon only + tooltip) */}
        <Tooltip title='Zoom on stenosis' arrow placement='top'>
          <Button
            variant='outlined'
            size='medium'
            onClick={onZoomStenosis}
            sx={{ ...btnSx, minWidth: 48, px: 0.75 }}
          >
            <PanoramaHorizontalSelectIcon sx={{ fontSize: 24 }} />
          </Button>
        </Tooltip>
        <Tooltip title='Zoom on bifurcation' arrow placement='top'>
          <Button
            variant='outlined'
            size='medium'
            onClick={onZoomBifurcation}
            sx={{ ...btnSx, minWidth: 48, px: 0.75 }}
          >
            <CallSplitIcon sx={{ fontSize: 24 }} />
          </Button>
        </Tooltip>
      </Box>

      {/* Row 1.5 — cycle info (split halves) */}
      <Box sx={{ display: 'flex', borderRadius: 0, overflow: 'hidden' }}>
        <Box
          sx={{
            flex: 1,
            bgcolor: `${alpha(theme.palette.primary.main, 0.1)}`,
            border: '1px solid rgba(255,255,255,0.08)',
            px: 2,
            py: 0.6,
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.78rem',
              color: 'background.default',
              whiteSpace: 'nowrap',
            }}
          >
            Flow status:{' '}
            <Box
              component='span'
              sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}
            >
              {fStatus}
            </Box>
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            bgcolor: `${alpha(theme.palette.primary.main, 0.1)}`,
            border: '1px solid rgba(255,255,255,0.08)',
            px: 2,
            py: 0.6,
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.78rem',
              color: 'background.default',
              whiteSpace: 'nowrap',
            }}
          >
            Current phase:{' '}
            <Box
              component='span'
              sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}
            >
              {timePhase}
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* Row 2 — nav buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <Button
          variant='outlined'
          size='small'
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
          onClick={onPrevious}
          disabled={!canGoBack}
          sx={btnSx}
        >
          Prev
        </Button>
        <Button
          variant='contained'
          size='small'
          startIcon={<PlayArrowIcon sx={{ fontSize: 16 }} />}
          onClick={onSimulate}
          sx={{
            ...btnSx,
            bgcolor: 'primary.main',
            color: '#fff',
            borderColor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
              borderColor: 'primary.dark',
            },
          }}
        >
          Simulate
        </Button>
        <Button
          variant='outlined'
          size='small'
          endIcon={<ArrowForwardIosIcon sx={{ fontSize: 14 }} />}
          onClick={onNext}
          disabled={!canGoForward}
          sx={btnSx}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}
